/* eslint-disable formatjs/no-literal-string-in-jsx */
import React, { FunctionComponent } from 'react'
import { useIntl } from 'react-intl'

import { createColumnHelper } from '@tanstack/react-table'
import { isBefore } from 'date-fns'
import styled, { useTheme } from 'styled-components/native'

import { targetUtilization, weekUtilizationData } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { Display } from '../../atoms/display/Display'
import { DropdownButton, DropdownMenuItem } from '../../atoms/dropdownButton/DropdownButton'
import { InfoIcon } from '../../atoms/icons/InfoIcon'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { Title } from '../../atoms/title/Title'
import { Tooltip } from '../../atoms/tooltip/Tooltip'
import { DataTable } from '../../molecules/dataTable/DataTable'
import { InlineInfoBanner } from '../../molecules/inlineInfoBanner/InlineInfoBanner'
import { BodyTextSize, DisplaySize, TitleSize } from '../../styles/typeStyles'
import { Flex1View } from '../../templates/content/CommonViews'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'

export interface DetailedWeeklyViewProps {
  historicUtilization?: weekUtilizationData[]
  targetUtilization?: targetUtilization
  historicQuarter?: string
  currentQuarter?: string
  dropdownItems: DropdownMenuItem[]
  expectedWorkingHours: number
  weeklyExpectedBookable: number
  isLoading?: boolean
  preselectedValue?: string
}

const Container = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  maxWidth: '1275px',
  marginLeft: '84px',
  marginRight: '84px',
  flexGrow: 1,
  gap: theme.spacing['32px'],
  paddingVertical: theme.spacing['32px'],
}))

const CardContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  backgroundColor: theme.colors.backgroundPrimary,
  padding: `${theme.spacing['32px']} ${theme.spacing['32px']}`,
  borderRadius: '16px',
  border: `1px solid ${theme.colors.borderDefault}`,
}))

const DetailsTableViewHeader = styled.View({
  textAlign: 'center',
  flexDirection: 'row',
  alignItems: 'center',
})

const DetailsTableViewTitle = styled(Display)({
  textAlign: 'center',
})

const DetailsTableViewHeaderTooltip = styled(Tooltip)({
  marginLeft: '4px',
})

const FlexRowContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  paddingBottom: theme.spacing['32px'],
  justifyContent: 'space-between',
  flexDirection: 'row',
  alignItems: 'center',
  zIndex: 1,
}))

const InlineBannerContainer = styled.View({
  marginTop: '8px',
  marginBottom: '24px',
})

const DropdownContainer = styled.View<{ theme: ThemeType }>(({ theme }) => ({
  marginRight: theme.spacing['16px'],
  position: 'relative',
  display: 'flex',
  flexDirection: 'row',
}))

const DropwdownLabel = styled.View({
  justifyContent: 'center',
  marginRight: '8px',
})

export const DetailedWeeklyView: FunctionComponent<DetailedWeeklyViewProps> = ({
  historicUtilization,
  targetUtilization,
  historicQuarter,
  currentQuarter,
  dropdownItems,
  expectedWorkingHours,
  weeklyExpectedBookable,
  isLoading,
  preselectedValue,
}) => {
  // @ts-ignore-next-line Property 'colors' does not exist on type 'DefaultTheme'.ts(2339)
  const { colors } = useTheme()
  const { formatDate, formatMessage } = useIntl()

  const columnHelper = createColumnHelper<weekUtilizationData>()

  /**
   * this function calculates the Bookable Target for each week the Provider works. The formula is:
   * Bookable Target = (([Expected Working Hours] - [Time Off Hours]) / [Expected Working Hours]) * [Weekly Expected Bookable]
   * @param timeOffHours<number>
   * @returns bookableTarget<number>
   */
  const getBookableTarget = (timeOffHours: number) => {
    return Math.ceil(((expectedWorkingHours - timeOffHours) / expectedWorkingHours) * weeklyExpectedBookable)
  }

  const columns = [
    columnHelper.accessor('startDate', {
      id: 'startDateRange',
      header: 'Dates',
      cell: (item) => {
        const startDate = new Date(item.getValue())
        const endDate = new Date(item.row.original.endDate)
        const isSameYear = startDate.getFullYear() === endDate.getFullYear()
        const formattedStart = isSameYear
          ? formatDate(startDate, { month: 'numeric', day: 'numeric' })
          : formatDate(startDate, { month: 'numeric', day: 'numeric', year: '2-digit' })
        const formattedEnd = isSameYear
          ? formatDate(endDate, { month: 'numeric', day: 'numeric' })
          : formatDate(endDate, { month: 'numeric', day: 'numeric', year: '2-digit' })
        return (
          <>
            {formattedStart}-{formattedEnd}
          </>
        )
      },
    }),
    columnHelper.accessor('providerPaidTimeOffHours', {
      header: () => (
        <DetailsTableViewHeader>
          <DetailsTableViewTitle text='Time off' size={DisplaySize.EYEBROW} color={colors.textPrimary} />
          <DetailsTableViewHeaderTooltip content='Based on Time Off hours blocked on your Google Calendar' hoverEnabled>
            <InfoIcon size={18} />
          </DetailsTableViewHeaderTooltip>
        </DetailsTableViewHeader>
      ),
      cell: (cell) => <>{cell.getValue() || 0} hrs</>,
    }),
    columnHelper.accessor('bookableExpected', {
      header: () => (
        <DetailsTableViewHeader>
          <DetailsTableViewTitle text='Calendar spots expected' size={DisplaySize.EYEBROW} color={colors.textPrimary} />
          <DetailsTableViewHeaderTooltip
            content='Expected count of calendar spots (scheduled, available, or attended): based on Time Off entered'
            hoverEnabled
          >
            <InfoIcon size={18} />
          </DetailsTableViewHeaderTooltip>
        </DetailsTableViewHeader>
      ),
      cell: (cell) => <>{getBookableTarget(parseInt(cell.getValue() ?? '0', 10))} hrs</>,
      id: 'bookableExpected',
    }),
    columnHelper.accessor('bookable', {
      header: () => (
        <DetailsTableViewHeader>
          <DetailsTableViewTitle text='Calendar spots' size={DisplaySize.EYEBROW} color={colors.textPrimary} />
          <DetailsTableViewHeaderTooltip
            content='Total count of actual calendar spots (scheduled, available, or attended)'
            hoverEnabled
          >
            <InfoIcon size={18} />
          </DetailsTableViewHeaderTooltip>
        </DetailsTableViewHeader>
      ),
      cell: (cell) => <>{cell.getValue()} hrs</>,
      id: 'bookable',
    }),
    columnHelper.accessor('providerAvailableEvents', {
      header: () => (
        <DetailsTableViewHeader>
          <DetailsTableViewTitle text='Available' size={DisplaySize.EYEBROW} color={colors.textPrimary} />
          <DetailsTableViewHeaderTooltip
            content='Total count of open spots on your calendar, available for clients to book'
            hoverEnabled
          >
            <InfoIcon size={18} />
          </DetailsTableViewHeaderTooltip>
        </DetailsTableViewHeader>
      ),
      id: 'available',
      cell: (cell) => <>{cell.getValue()}</>,
    }),
    columnHelper.accessor('sessionsAttended', {
      header: () => (
        <DetailsTableViewHeader>
          <DetailsTableViewTitle text='Attended' size={DisplaySize.EYEBROW} color={colors.textPrimary} />
          <DetailsTableViewHeaderTooltip content='Total count of sessions closed as Attended' hoverEnabled>
            <InfoIcon size={18} />
          </DetailsTableViewHeaderTooltip>
        </DetailsTableViewHeader>
      ),
      id: 'attended',
      cell: (cell) => <>{cell.getValue()}</>,
    }),
    columnHelper.accessor('sessionsUnattended', {
      header: () => (
        <DetailsTableViewHeader>
          <DetailsTableViewTitle text='Unattended' size={DisplaySize.EYEBROW} color={colors.textPrimary} />
          <DetailsTableViewHeaderTooltip
            content='Total count of No Shows, Provider Cancels, Client Cancels, Med Emergency Cancels, Care Transitions, and Tech Issue Cancels'
            hoverEnabled
          >
            <InfoIcon size={18} />
          </DetailsTableViewHeaderTooltip>
        </DetailsTableViewHeader>
      ),
      id: 'unattended',
      cell: (cell) => <>{cell.getValue()}</>,
    }),
  ]

  const updatedData =
    historicUtilization?.map((row, index) => {
      const target = targetUtilization
        ? Math.round(
            (targetUtilization?.providerExpectedWeekWorkHours -
              (row.providerPaidTimeOffHours ? parseFloat(row.providerPaidTimeOffHours) : 0)) *
              0.75,
          )
        : 0
      const pto = row.providerPaidTimeOffHours
        ? parseFloat(Math.round(2 * parseFloat(row.providerPaidTimeOffHours)).toFixed(0)) / 2
        : 0
      const available =
        typeof row.providerAvailableEvents === 'string'
          ? parseInt(row.providerAvailableEvents, 10)
          : (row.providerAvailableEvents ?? 0)
      const bookable =
        available + (row?.sessionsScheduled ?? 0) + (row?.sessionsNoShow ?? 0) + (row?.sessionsAttended ?? 0)
      return {
        week: 'Week ' + (index + 1),
        startDate: row.startDate,
        endDate: row.endDate,
        sessionsCredited: row.sessionsCredited,
        sessionsNonCredited: row.sessionsNonCredited,
        providerPaidTimeOffHours: pto.toString(),
        bookableExpected: pto.toString(),
        bookable: bookable,
        target: target.toString(),
        achieved_target: isBefore(new Date(), new Date(row.endDate))
          ? '-'
          : row.sessionsCredited >= target
            ? 'Yes'
            : 'No',
        providerAvailableEvents: available,
        sessionsAttended: row?.sessionsAttended ?? 0,
        sessionsUnattended: row?.sessionsUnattended ?? 0,
      }
    }) ?? []

  return (
    <Container>
      <CardContainer>
        <FlexRowContainer>
          <Title
            size={TitleSize.MEDIUM}
            text='Detailed quarterly view'
            testID={tID('CaseloadManagementDetailedQuarterlyView-title')}
          />
          <DropdownContainer>
            <DropwdownLabel>
              <BodyText
                size={BodyTextSize.LARGE}
                text={formatMessage({
                  defaultMessage: 'View quarter',
                  description: 'a label to tell the user to select a quarter of data to look at',
                })}
              />
            </DropwdownLabel>
            <DropdownButton dropdownItems={dropdownItems} dropdownWidth={185} preselectedValue={preselectedValue} />
          </DropdownContainer>
        </FlexRowContainer>
        {historicQuarter && historicQuarter !== currentQuarter && (
          <InlineBannerContainer testID={tID('WeeklySnapshot-historicInlineBanner')}>
            <InlineInfoBanner
              marginBottom={'0px'}
              text={`You are viewing ${historicQuarter.replace(
                '_',
                ' ',
              )} data. If manual adjustments were made after the quarter they will not be reflected here.`}
            />
          </InlineBannerContainer>
        )}
        {isLoading ? (
          <Flex1View>
            <LoadingIndicator topPadding={24} />
          </Flex1View>
        ) : (
          <DataTable columns={columns} data={updatedData} rowHeight={48} />
        )}
      </CardContainer>
    </Container>
  )
}
